import { Component, Fragment, type ReactElement } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy.js';
import classnames from 'classnames';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';
import { Link } from 'dibs-elements/exports/Link.tsx';
import LazyImage from 'dibs-react-lazy-image';
import LeftArrow from 'dibs-icons/exports/legacy/ArrowLeft.ts';
import RightArrow from 'dibs-icons/exports/legacy/ArrowRight.ts';

import styles from './styles/DesignerDirectoryImageCarousel.scss';

import { type DesignerDirectoryImageCarousel_firm$data } from './__generated__/DesignerDirectoryImageCarousel_firm.graphql.ts';

type DesignerDirectoryImageCarouselProps = {
    firm: DesignerDirectoryImageCarousel_firm$data;
};

type DesignerDirectoryImageCarouselState = {
    index: number;
};

const IMAGE_COUNT = 3;
const LAZY_LOAD_OFFSET = 400;

const getImagePath = (src: string): string => `${src}?width=470&crop=470:250&format=pjpg&auto=webp`;

export class DesignerDirectoryImageCarouselComponent extends Component<
    DesignerDirectoryImageCarouselProps,
    DesignerDirectoryImageCarouselState
> {
    constructor(props: DesignerDirectoryImageCarouselProps) {
        super(props);

        this.state = {
            index: 0,
        };
    }

    componentDidMount(): void {
        const images = this.props.firm?.directoryProfile?.projectCoverPhotos || [];
        images.slice(0, IMAGE_COUNT).forEach(imageSrc => {
            if (imageSrc) {
                let img = null;
                img = new Image();
                img.src = getImagePath(imageSrc);
                img.onload = () => {
                    img = null;
                };
            }
        });
    }

    next = (): void => {
        const { index } = this.state;
        if (index === IMAGE_COUNT - 1) {
            this.setState({ index: 0 });
        } else {
            this.setState({ index: index + 1 });
        }
    };

    prev = (): void => {
        const { index } = this.state;
        if (index === 0) {
            this.setState({ index: IMAGE_COUNT - 1 });
        } else {
            this.setState({ index: index - 1 });
        }
    };

    renderImages = (): ReactElement[] | ReactElement | null => {
        const { index } = this.state;
        const images = (this.props.firm?.directoryProfile?.projectCoverPhotos || []).filter(
            filterNulls
        );
        const name = this.props.firm?.name || '';

        //if the firm has the minimum number of images, show them
        if (images.length >= IMAGE_COUNT) {
            return images.slice(0, IMAGE_COUNT).map((src, i) => {
                const image = (
                    <img
                        className={classnames(styles.projectImage, {
                            [styles.transparent]: i !== index,
                        })}
                        height={250}
                        src={getImagePath(src)}
                        alt={`${name} ${i + 1}`}
                    />
                );

                return (
                    <Fragment key={`${name}-${i}`}>
                        <noscript>{image}</noscript>
                        <LazyImage
                            offsetVertical={LAZY_LOAD_OFFSET}
                            placeholderClass={styles.placeholder}
                        >
                            {image}
                        </LazyImage>
                    </Fragment>
                );
            });
        }

        //else just show the first image
        if (images[0]) {
            const image = (
                <img
                    className={styles.projectImage}
                    src={getImagePath(images[0])}
                    alt={`${name} 1`}
                />
            );

            return (
                <Fragment>
                    <noscript>{image}</noscript>
                    <LazyImage
                        offsetVertical={LAZY_LOAD_OFFSET}
                        placeholderClass={styles.placeholder}
                    >
                        {image}
                    </LazyImage>
                </Fragment>
            );
        }

        return null;
    };

    renderControls = (): ReactElement | null => {
        const { index } = this.state;
        const name = this.props.firm?.name || '';
        const urlLabel = this.props.firm?.publicProfile?.urlLabel;
        const images = this.props.firm?.directoryProfile?.projectCoverPhotos || [];

        /* eslint-disable jsx-a11y/mouse-events-have-key-events */
        const imageIndicatorsWrapper = (
            <div className={styles.indicatorsWrapper}>
                {images.slice(0, IMAGE_COUNT).map((el, i) => (
                    <div
                        className={styles.imageIndicator}
                        onMouseOver={() => i > 0 && this.setState({ index: i })}
                        onMouseOut={() => i > 0 && this.setState({ index: 0 })}
                        key={`${name}-${i}`}
                        data-tn="design-firms-image-carousel-indicator"
                    >
                        <div
                            className={classnames(styles.imageIndicatorRect, {
                                [styles.currentImageIndicator]: i === index,
                            })}
                            data-tn="design-directory-image"
                        />
                    </div>
                ))}
            </div>
        );
        /* eslint-enable jsx-a11y/mouse-events-have-key-events */

        if (images.length >= IMAGE_COUNT) {
            /* eslint-disable jsx-a11y/interactive-supports-focus,jsx-a11y/click-events-have-key-events,jsx-a11y/mouse-events-have-key-events,jsx-a11y/no-static-element-interactions */
            return (
                <>
                    {urlLabel ? (
                        <Link href={`/design-firms/${urlLabel}/`}>{imageIndicatorsWrapper}</Link>
                    ) : (
                        imageIndicatorsWrapper
                    )}
                    <div
                        className={styles.arrowLeft}
                        data-tn="left-arrow"
                        onClick={() => this.prev()}
                    >
                        <LeftArrow className={styles.arrowSVG} />
                    </div>
                    <div
                        className={styles.arrowRight}
                        data-tn="right-arrow"
                        onClick={() => this.next()}
                    >
                        <RightArrow className={styles.arrowSVG} />
                    </div>
                </>
            );
            /* eslint-enable jsx-a11y/interactive-supports-focus,jsx-a11y/click-events-have-key-events,jsx-a11y/mouse-events-have-key-events,jsx-a11y/no-static-element-interactions */
        } else if (urlLabel) {
            /* eslint-disable jsx-a11y/anchor-has-content */
            return (
                <Link href={`/design-firms/${urlLabel}/`} className={styles.indicatorsWrapper} />
            );
            /* eslint-enable jsx-a11y/anchor-has-content */
        }

        return null;
    };

    render(): ReactElement | null {
        const images = this.props.firm?.directoryProfile?.projectCoverPhotos || [];
        if (images.length === 0) {
            return null;
        }

        return (
            <div className={styles.wrapper}>
                {this.renderImages()}
                {this.renderControls()}
            </div>
        );
    }
}

export const DesignerDirectoryImageCarousel = createFragmentContainer(
    DesignerDirectoryImageCarouselComponent,
    {
        firm: graphql`
            fragment DesignerDirectoryImageCarousel_firm on DirectoryDesignFirm {
                name
                directoryProfile {
                    projectCoverPhotos
                }
                publicProfile {
                    urlLabel
                }
            }
        `,
    }
);
